import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, getPermission, subscribePush, hasEverEnabled } from "@/lib/push";

const DISMISS_KEY = "signalagent_push_banner_dismissed_at";
const DISMISS_DAYS = 7;

export default function NotificationBanner() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    const perm = getPermission();
    if (perm !== "default") return;
    if (hasEverEnabled()) return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_DAYS * 24 * 3600 * 1000) {
      return;
    }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setLoading(true);
    try {
      await subscribePush();
      toast.success("Notifications activées.");
      setVisible(false);
    } catch (e) {
      toast.error(e.message || "Impossible d'activer les notifications.");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div
      data-testid="push-banner"
      className="mx-auto mb-4 flex max-w-xl items-start gap-3 rounded-[14px] border border-[var(--sa-blue-soft)] bg-white px-4 py-3.5 shadow-[0_4px_20px_-8px_rgba(37,99,235,0.18)]"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--sa-blue-soft)] text-[var(--sa-blue)]">
        <Bell size={18} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-[13.5px] font-semibold text-slate-900">
          Activer les notifications urgentes
        </p>
        <p className="mt-0.5 text-[12.5px] text-slate-500 leading-snug">
          Recevez un signal immédiat quand un collègue déclenche un feu orange ou rouge.
        </p>
        <div className="mt-2.5 flex gap-2">
          <button
            onClick={handleEnable}
            disabled={loading}
            data-testid="push-banner-enable"
            className="rounded-full bg-[var(--sa-blue)] px-3.5 py-1.5 text-[12px] font-semibold text-white transition hover:bg-[var(--sa-blue-dark)] disabled:opacity-60"
          >
            {loading ? "Activation…" : "Activer"}
          </button>
          <button
            onClick={handleDismiss}
            data-testid="push-banner-dismiss"
            className="rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Fermer"
        className="-mr-1 text-slate-400 transition hover:text-slate-600"
      >
        <X size={16} />
      </button>
    </div>
  );
}
